import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { FollowUpActionListItem } from 'src/types/myFollowUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import type { FilterValue } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import { FILTER_KEYS } from 'src/components/followUp/hooks/constants';
import type { DateRangeAsStrings } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import { dateRangeFilterToApiString } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

export const useFollowUpActionListQuery = (
  offset: number,
  ordering: string,
  limit = 10,
  enabled = true,
) => {
  const { filterGroups } = useFilterContext();

  const dateRange = useMemo(
    () =>
      filterGroups[FILTER_KEYS.DATE_RANGE]?.[0]
        ? dateRangeFilterToApiString(
            filterGroups[
              FILTER_KEYS.DATE_RANGE
            ][0] as FilterValue<DateRangeAsStrings>,
          )
        : undefined,
    [filterGroups[FILTER_KEYS.DATE_RANGE]],
  );

  const orgUnit = useMemo(
    () =>
      filterGroups[ORG_UNIT_FILTER_NAME]?.map(
        (orgUnit) => orgUnit.filterExpr.orgUnitDbId,
      ).toString(),
    [filterGroups],
  );

  const filters = [
    'search',
    'kpi',
    'followUpOwner',
    'status',
    'hasPriorityOrgUnit',
  ];
  const appliedFilters = Object.fromEntries(
    filters.map((filterKey) => [
      filterKey,
      filterGroups[filterKey]
        ?.map(({ filterExpr }) => Object.values(filterExpr))
        .toString(),
    ]),
  );

  return useQuery<
    {
      count: number;
      results: FollowUpActionListItem[];
      next: string;
      previous: string;
    },
    string
  >({
    queryKey: [
      apiPaths.FOLLOW_UP_ACTIONS.LIST,
      offset,
      ordering,
      limit,
      dateRange,
      orgUnit,
      appliedFilters,
    ],
    enabled,
    queryFn: ({ signal }) =>
      HttpService.get(
        apiPaths.FOLLOW_UP_ACTIONS.LIST,
        {
          limit,
          offset,
          ordering:
            ordering === 'follow_up_owner'
              ? 'follow_up_owner__first_name, follow_up_owner__last_name'
              : ordering === '-follow_up_owner'
                ? '-follow_up_owner__first_name, -follow_up_owner__last_name'
                : ordering,
          ...dateRange,
          orgUnit,
          ...appliedFilters,
        },
        true,
        { signal },
      )
        .then((data) => Promise.resolve(data.data))
        .catch(({ response }) => {
          if (
            response.status >= 500 ||
            response.status === 424 ||
            response.status === 404 ||
            response.status === 400
          ) {
            return Promise.reject('somethingWentWrong');
          }
          if (Object.hasOwn(response.data, 'detail')) {
            return Promise.reject(String(response.data.detail));
          }
          return Promise.reject(String(response.data));
        }),
  });
};
